/* eslint-disable react/prop-types */
import React, { Suspense, useEffect, lazy } from 'react';
import _map from 'lodash/map';
import _isEmpty from 'lodash/isEmpty';
import _sortBy from 'lodash/sortBy';
import _compact from 'lodash/compact';
import _join from 'lodash/join';
import { string } from 'prop-types';
import { useStore } from '@thd-nucleus/experience-context';
import { Typography } from '@one-thd/sui-atomic-components';
import { normalizeOptionData } from '@gcc/configurator/dist/modules';
import { getOptionsChoicesAndMetadata, tryGetMultipleDesigns } from '../../api';
import { useAsyncServiceGCC, useAsyncServiceTHD } from '../../hooks';
import { useExtractDimensions } from '../../hooks/useExtractDimensions';
import { mapItemIds } from '../../utils/mapItemIds';
import { normalizeMetadata } from '../../utils/normalizeMetadata';
import logNewRelicCustom from '../../utils/logNewRelicCustom';
import {
  useConfiguratorStore, useDimensionsStore, useProductInfoStore,
  useDigitalDataStore, useProductDataStore
} from '../../store';
import { useDesignStore } from '../../store/design/context';
import { DesktopOption, MobileOption } from '../../templates/option';
import Placeholders from '../../placeholders/Placeholders';
import Compatibility from '../Compatibility';
import { TrackPageLoad } from '../Analytics';
import { useQuickViewPipClickThroughEvent } from './useQuickViewPipClickThroughEvent';
import { useQuickViewClickSectionEvent } from './useQuickViewClickSectionEvent';

import '../../styles/product/configurator.scss';
import { DrawerLoader } from '../BundleDrawer/index';
import { NEW_RELIC_EVENTS } from '../../constants';

const BundleDrawerLazy = lazy(() => import('../BundleDrawer/index'));

const Configurator = (props) => {
  const store = useStore();
  const { itemId, selectedId, design, isDesktop } = props;
  const [{
    data: optionsChoicesAndMetadata,
    ready: optionsChoicesAndMetadataHydrated
  }] = useAsyncServiceGCC(getOptionsChoicesAndMetadata, itemId);

  const {
    configurator,
    updateConfigurator,
  } = useConfiguratorStore((state) => state);

  const updateBundles = useDesignStore((state) => state.updateBundles);
  const productInfo = useProductInfoStore((state) => state.productInfo);

  const configuratorMetadata = configurator?.metadata || [];
  const bundleDesignIds = _join(_compact(_map(configuratorMetadata, (data) => data?.metadata?.BundleDesign)), ',');
  const [{
    data: bundleDesigns,
    ready: bundleDesignsHydrated
  }] = useAsyncServiceGCC(tryGetMultipleDesigns, bundleDesignIds);

  const {
    trackPageLoadWithProductImpressionsEvent,
    trackQuickViewSectionViewedEvent,
    trackQuickViewPipClickThroughEvent
  } = useDigitalDataStore((state) => state);
  const { initializeSkuDimensions } = useDimensionsStore((state) => state);
  const { updateProductData } = useProductDataStore((state) => state);
  const [{
    data: productData
  }] = useAsyncServiceTHD(configurator?.itemIds);

  useExtractDimensions(
    productData,
    updateProductData,
    initializeSkuDimensions,
    trackPageLoadWithProductImpressionsEvent,
    store,
    configurator
  );

  useQuickViewClickSectionEvent(trackQuickViewSectionViewedEvent);

  useQuickViewPipClickThroughEvent(trackQuickViewPipClickThroughEvent);

  useEffect(() => {
    if (
      optionsChoicesAndMetadataHydrated
      && !_isEmpty(optionsChoicesAndMetadata)
    ) {
      const normalized = normalizeOptionData(optionsChoicesAndMetadata?.groups);

      if (!_isEmpty(normalized)) {
        const sortedOptions = _sortBy(normalized.options, (option) => option.groupSequence);
        updateConfigurator({
          options: { ...sortedOptions },
          choices: { ...normalized.choices },
          itemIds: mapItemIds(normalized.choices),
          metadata: normalizeMetadata(normalized.choices)
        });
        const optionIds = _map(sortedOptions, 'id');
        const choiceIds = _map(normalized.choices, 'id');
        logNewRelicCustom(NEW_RELIC_EVENTS.OPTIONS_CHOICES_HYDRATED, { optionIds, choiceIds });
      }
    }
  }, [optionsChoicesAndMetadataHydrated]);

  useEffect(() => {
    if (
      bundleDesignsHydrated
      && !_isEmpty(bundleDesigns)
    ) {
      updateBundles(bundleDesigns);
    }
  }, [bundleDesignsHydrated]);

  if (
    !optionsChoicesAndMetadataHydrated
    || !configurator.options
    || !configurator.choices
  ) {
    return (
      <Placeholders
        rows={[1, 2, 3, 4]}
      />
    );
  }

  return (
    <div className="sui-block configurator">
      <TrackPageLoad optionsChoicesAndMetadata={optionsChoicesAndMetadata} />
      <Compatibility />
      <Suspense fallback={<DrawerLoader />}>
        <BundleDrawerLazy />
      </Suspense>
      {isDesktop ? (
        <div style={{ minHeight: '100vh' }}>
          <Typography variant="h3" style={{ marginBottom: '1rem' }}>
            Customize Your {productInfo?.displayName}
          </Typography>
          {_map(configurator.options, (option) => {
            return (
              <div key={option.internalName}>
                <DesktopOption
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...props}
                  choices={configurator.choices}
                  option={option}
                  selectedId={selectedId}
                  design={design}
                />
              </div>
            );
          })}
        </div>
      ) : (
        <MobileOption
          design={design}
          choices={configurator.choices}
          options={configurator.options}
        />
      )}
    </div>
  );
};

Configurator.propTypes = {
  name: string
};

Configurator.displayName = 'Configurator';

Configurator.defaultProps = {
  name: 'Configurator',
};

export default Configurator;
